<template>
	<div>
		<section
			class="ts-bg-gray-200 ts-rounded-xl ts-border ts-border-slate-200 dark:ts-border-slate-700 ts-overflow-hidden ts-shadow-sm">
		<div
			class="ts-px-6 ts-py-3 ts-border-b ts-border-slate-100 dark:ts-border-slate-700/50 ts-flex ts-items-center ts-justify-between ts-bg-slate-50 dark:ts-bg-slate-800">
			<h2 class="ts-font-semibold ts-text-slate-700 dark:ts-text-slate-200 ts-flex ts-items-center ts-gap-2 ts-text-sm">
				<span class="material-icons ts-text-base">history</span>
				{{ $t('invoiceAdjustment.old_data_reference') }}
			</h2>
			<div class="ts-flex ts-items-center ts-gap-3">
				<button v-if="invoice && invoice.invoice_number"
					class="ts-px-3 ts-py-1 ts-bg-blue-100 dark:ts-bg-blue-900/30 ts-text-blue-700 dark:ts-text-blue-300 ts-text-xs ts-font-medium ts-rounded-full focus:ts-outline-none">
					{{ invoice.invoice_number }}
				</button>
				<button @click="onShowModal"
					class="ts-px-2 ts-py-1 ts-bg-blue-500 hover:ts-bg-blue-700 ts-text-white ts-text-xs ts-font-semibold ts-rounded-md 
					ts-transition-all ts-inline-flex ts-items-center ts-gap-1 focus:ts-outline-none">
					<span class="material-icons ts-text-sm">{{ invoice ? 'edit' : 'search' }}</span>
					{{ invoice ? $t('invoiceAdjustment.changeInvoice') : $t('invoiceAdjustment.searchInvoice') }}
				</button>
			</div>
		</div>

		<!-- Empty State -->
		<div v-if="!invoice" class="ts-p-4 ts-bg-white dark:ts-bg-slate-900">
			<div class="ts-text-center ts-space-y-3">
				<span class="material-icons ts-text-slate-300 dark:ts-text-slate-600 ts-text-5xl">search</span>
				<p class="ts-text-sm ts-font-medium ts-text-slate-500 dark:ts-text-slate-400">{{ $t('invoiceAdjustment.noInvoiceSelected') }}</p>
				<p class="ts-text-xs ts-text-slate-400 dark:ts-text-slate-500">{{ $t('invoiceAdjustment.searchAndSelectInvoice') }}</p>
			</div>
		</div>

		<!-- Invoice Data -->
		<div v-else class="ts-p-4 ts-grid ts-grid-cols-2 md:ts-grid-cols-4 lg:ts-grid-cols-8 ts-gap-4 ts-bg-white dark:ts-bg-slate-900">
			<div>
				<label class="text-10 ts-font-bold ts-uppercase ts-tracking-wider ts-mb-1 ts-block ts-text-gray-500">
					{{ $t('invoiceAdjustment.studentName') }}</label>
				<p class="ts-text-sm ts-font-medium ts-text-slate-700 dark:ts-text-slate-300">{{ invoice.student_name || '-' }}</p>
			</div>
			<div>
				<label class="text-10 ts-font-bold ts-uppercase ts-tracking-wider ts-mb-1 ts-block ts-text-gray-500">
					{{ $t('invoiceAdjustment.cardNumber') }}</label>
				<p class="ts-text-sm ts-font-medium ts-text-slate-700 dark:ts-text-slate-300">{{ invoice.card_number || '-' }}</p>
			</div>
			<div>
				<label class="text-10 ts-font-bold ts-uppercase ts-tracking-wider ts-mb-1 ts-block ts-text-gray-500">
					{{ $t('invoiceAdjustment.main_program') }}</label>
				<p class="ts-text-sm ts-font-medium ts-text-slate-700 dark:ts-text-slate-300">{{ invoice.main_program || '-' }}</p>
			</div>
			<div>
				<label class="text-10 ts-font-bold ts-uppercase ts-tracking-wider ts-mb-1 ts-block ts-text-gray-500">
					{{ $t('invoiceAdjustment.sub_program') }}
				</label>
				<p class="ts-text-sm ts-font-medium ts-text-slate-700 dark:ts-text-slate-300">{{ invoice.sub_program || '-' }}</p>
			</div>
			<div>
				<label class="text-10 ts-font-bold ts-uppercase ts-tracking-wider ts-mb-1 ts-block ts-text-gray-500">
					{{ $t('invoiceAdjustment.level') }}
				</label>
				<p class="ts-text-sm ts-font-medium ts-text-slate-700 dark:ts-text-slate-300">{{ invoice.level || '-' }}</p>
			</div>
			<div>
				<label class="text-10 ts-font-bold ts-uppercase ts-tracking-wider ts-mb-1 ts-block ts-text-gray-500">
					{{ $t('invoiceAdjustment.grade') }}</label>
				<p class="ts-text-sm ts-font-medium ts-text-slate-700 dark:ts-text-slate-300">{{ invoice.grade || '-' }}</p>
			</div>
			<div>
				<label class="text-10 ts-font-bold ts-uppercase ts-tracking-wider ts-mb-1 ts-block ts-text-gray-500">
					{{ $t('invoiceAdjustment.class') }}</label>
				<p class="ts-text-sm ts-font-medium ts-text-slate-700 dark:ts-text-slate-300">{{ invoice.class_name || '-' }}</p>
				<p class="ts-text-xs ts-bg-blue-100 ts-text-blue-600 ts-rounded-full ts-px-2 ts-py-1" style="width: fit-content;">{{ invoice.class_desc || '-' }}</p>
			</div>
			<div>
				<label class="text-10 ts-font-bold ts-uppercase ts-tracking-wider ts-mb-1 ts-block ts-text-gray-500">
					{{ $t('invoiceAdjustment.invoice_amount') }}</label>
				<p class="ts-text-sm ts-font-bold ts-text-primary">{{ formatCurrency(invoice.amount) }}</p>
			</div>
		</div>
	</section>

	<!-- Invoice Selection Modal -->
	<a-modal v-model="showModal" :title="$t('invoiceAdjustment.searchInvoice')" width="1200px" :footer="null">
		<div class="ts-space-y-4">
			<!-- Search Input -->
			<div class="ts-flex ts-gap-3">
				<a-input-search v-model="searchQuery" :placeholder="$t('invoiceAdjustment.searchByInvoiceOrStudent')"
					size="large" @search="fetchInvoices" @pressEnter="fetchInvoices">
					<a-icon slot="prefix" type="search" />
				</a-input-search>
			</div>

			<!-- Loading State -->
			<div v-if="loading" class="ts-text-center ts-py-12">
				<a-spin size="large" />
			</div>

			<!-- Invoice Table -->
			<div v-else-if="invoices.length > 0" class="ts-overflow-x-auto">
				<table class="ts-w-full ts-text-left ts-border-collapse ts-min-w-[900px]">
					<thead>
						<tr
							class="ts-bg-slate-50 dark:ts-bg-slate-900 ts-text-xs ts-font-bold ts-uppercase ts-tracking-wider ts-text-slate-500 dark:ts-text-slate-400 ts-border-b ts-border-slate-200 dark:ts-border-slate-700">
							<th class="ts-px-4 ts-py-3">{{ $t('invoiceAdjustment.invoiceDate') }}</th>
							<th class="ts-px-4 ts-py-3">{{ $t('invoiceAdjustment.invoiceNumber') }}</th>
							<th class="ts-px-4 ts-py-3">{{ $t('invoiceAdjustment.cardNumber') }}</th>
							<th class="ts-px-4 ts-py-3">{{ $t('invoiceAdjustment.studentName') }}</th>
							<th class="ts-px-4 ts-py-3">{{ $t('invoiceAdjustment.grade') }}</th>
							<th class="ts-px-4 ts-py-3">{{ $t('invoiceAdjustment.class') }}</th>
							<th class="ts-px-4 ts-py-3 ts-text-right">{{ $t('invoiceAdjustment.totalAmount') }}</th>
						</tr>
					</thead>
					<tbody class="ts-divide-y ts-divide-slate-100 dark:ts-divide-slate-700">
						<tr v-for="inv in invoices" :key="inv.inv_id" @click="selectInvoice(inv)"
							class="invoice-row ts-transition-colors ts-cursor-pointer">
							<td class="ts-px-4 ts-py-3 ts-text-sm">{{ inv.inv_date }}</td>
							<td class="ts-px-4 ts-py-3 ts-text-sm ts-font-medium ts-text-blue-600">{{ inv.inv_number }}</td>
							<td class="ts-px-4 ts-py-3 ts-text-sm">{{ inv.card_number }}</td>
							<td class="ts-px-4 ts-py-3 ts-text-sm">{{ inv.first_name_en }} {{ inv.last_name_en }}</td>
							<td class="ts-px-4 ts-py-3 ts-text-sm">{{ inv.grade_name_foreign }}</td>
							<td class="ts-px-4 ts-py-3 ts-text-sm">
								<p>
									{{ inv.class_name }} - {{  inv.class_desc }}
								</p>
							</td>
							<td class="ts-px-4 ts-py-3 ts-text-sm ts-text-right ts-font-semibold">{{ formatCurrency(inv.total_amount)
								}}</td>
							
						</tr>
					</tbody>
				</table>
			</div>

			<!-- Empty State in Modal -->
			<div v-else class="ts-text-center ts-py-12">
				<span class="material-icons ts-text-slate-300 dark:ts-text-slate-600 ts-text-5xl">receipt_long</span>
				<p class="ts-mt-4 ts-text-sm ts-font-medium ts-text-slate-500 dark:ts-text-slate-400">{{
					$t('invoiceAdjustment.noInvoicesFound') }}</p>
				<p class="ts-text-xs ts-text-slate-400 dark:ts-text-slate-500">{{
					$t('invoiceAdjustment.tryDifferentSearch') }}</p>
			</div>
		</div>
	</a-modal>
	</div>
</template>

<script>
import { mapActions } from 'vuex';

export default {
	name: "ReferenceInvoice",
	props: {
		invoice: {
			type: Object,
			default: null
		}
	},
	data() {
		return {
			showModal: false,
			searchQuery: '',
			invoices: [],
			loading: false
		}
	},
	methods: {
		...mapActions('invoiceAjustment', ['onGetInvoice']),
		formatCurrency(amount) {
			if (!amount) return '$ 0.00'
			return `$ ${parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
		},
		async onShowModal() {
			this.showModal = true
			this.searchQuery = ''
			this.invoices = []
			await this.fetchInvoices()
		},
		async fetchInvoices() {
			this.loading = true
			try {
				const response = await this.onGetInvoice({ invoiceSearch: this.searchQuery })
				this.invoices = response.data.data || []
			} catch (error) {
				console.error('Error fetching invoices:', error)
				this.$message.error(this.$t('invoiceAdjustment.errorFetchingInvoices'))
			} finally {
				this.loading = false
			}
		},
		selectInvoice(inv) {
			const selectedInvoice = {
				inv_id: inv.inv_id,
				invoice_number: inv.inv_number,
				student_id: inv.student_id,
				student_name: `${inv.first_name_en} ${inv.last_name_en}`,
				card_number: `${inv.card_number}`,
				main_program: inv.acp_name_foreign,
				sub_program: inv.acps_name_foreign,
				level: inv.level_name_foreign,
				grade: inv.grade_name_foreign,
				grade_id: inv.grade_id,
				class_id: inv.class_id,
				class_name: inv.class_name,
				class_desc: inv.class_desc,
				amount: inv.total_amount,
				inv_date: inv.inv_date,
				term_name: inv.term_name,
				acp_id: inv.acp_id,
				invoiceDetail: inv.invoice_detail
			}

			this.$emit('invoice-selected', selectedInvoice)
			this.showModal = false
		}
	}
}
</script>

<style scoped>
.text-10 {
	font-size: 10px;
}

.invoice-row:hover {
	background-color: #eff6ff;
}

.dark .invoice-row:hover {
	background-color: rgba(30, 58, 138, 0.2);
}
</style>